import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  Share2, Plus, Facebook, Instagram, Twitter, Linkedin, Youtube,
  Calendar as CalendarIcon, Send, Image as ImageIcon, Clock, BarChart3, Inbox as InboxIcon,
  Users, TrendingUp, MessageSquare, Heart, Eye, RefreshCw, Settings, X, Trash2, Edit2, ArrowRight, ExternalLink, Globe, ChevronDown,
  Sparkles, Hash, Wand2, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  twitter: Twitter,
  linkedin: Linkedin,
  youtube: Youtube,
  google_business: Globe
};

const PLATFORM_COLORS = {
  facebook: 'bg-blue-600',
  instagram: 'bg-gradient-to-r from-purple-500 to-pink-500',
  twitter: 'bg-sky-500',
  linkedin: 'bg-blue-700',
  youtube: 'bg-red-600',
  google_business: 'bg-blue-500'
};

const SocialMediaDashboard = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [channels, setChannels] = useState([]);
  const [posts, setPosts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [showComposeDialog, setShowComposeDialog] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  
  // Compose post state
  const [postContent, setPostContent] = useState('');
  const [postTitle, setPostTitle] = useState('');
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [scheduledDate, setScheduledDate] = useState(null);
  const [scheduledTime, setScheduledTime] = useState('12:00');
  const [postMediaUrls, setPostMediaUrls] = useState([]);
  
  // AI Features state
  const [aiTopic, setAiTopic] = useState('');
  const [aiTone, setAiTone] = useState('professional');
  const [aiPlatform, setAiPlatform] = useState('instagram');
  const [generatingCaption, setGeneratingCaption] = useState(false);
  const [generatingHashtags, setGeneratingHashtags] = useState(false);
  const [captionVariations, setCaptionVariations] = useState([]);
  const [suggestedHashtags, setSuggestedHashtags] = useState([]);
  const [showAiPanel, setShowAiPanel] = useState(false);

  const fetchChannels = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/social-mgmt/channels?token=${token}`);
      const data = await res.json();
      if (data.success) {
        setChannels(data.channels || []);
      }
    } catch (error) {
      console.error('Error fetching channels:', error);
    }
  }, [token]);

  const fetchPosts = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/social-mgmt/posts?token=${token}&limit=10`);
      const data = await res.json();
      if (data.success) {
        setPosts(data.posts || []);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  }, [token]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/social-mgmt/analytics/overview?token=${token}&days=30`);
      const data = await res.json();
      if (data.success) {
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  }, [token]);

  const fetchPlatforms = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/social-mgmt/channels/platforms`);
      const data = await res.json();
      if (data.platforms) {
        setPlatforms(data.platforms);
      }
    } catch (error) {
      console.error('Error fetching platforms:', error);
    }
  }, []);

  // Progressive — flip the spinner off on the first tile that arrives
  // so a slow analytics aggregation doesn't freeze the whole page.
  useEffect(() => {
    setLoading(true);
    const tasks = [fetchChannels(), fetchPosts(), fetchAnalytics(), fetchPlatforms()]
      .map((p) => Promise.resolve(p).catch((e) => {
        console.error('[SocialMediaDashboard] tile failed:', e); return null;
      }));
    let done = false;
    const off = () => { if (!done) { done = true; setLoading(false); } };
    const safety = setTimeout(off, 3000);
    Promise.race(tasks).then(off);
    Promise.allSettled(tasks).finally(() => clearTimeout(safety));
  }, [fetchChannels, fetchPosts, fetchAnalytics, fetchPlatforms]);

  const handleConnectChannel = async () => {
    if (!selectedPlatform) {
      toast.error('Please select a platform');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/social-mgmt/channels/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          platform: selectedPlatform,
          auth_code: 'demo_auth_code' // In production, this comes from OAuth flow
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(`${selectedPlatform} channel connected successfully`);
        setShowConnectDialog(false);
        setSelectedPlatform(null);
        fetchChannels();
      } else {
        // Show OAuth URL if available
        if (data.oauth_url) {
          window.open(data.oauth_url, '_blank', 'width=600,height=700');
        } else {
          toast.error(data.message || 'Failed to connect channel');
        }
      }
    } catch (error) {
      toast.error('Error connecting channel');
    }
  };

  const handleCreatePost = async (publish = false) => {
    if (!postContent.trim()) {
      toast.error('Please enter post content');
      return;
    }

    if (selectedChannels.length === 0) {
      toast.error('Please select at least one channel');
      return;
    }

    try {
      const postData = {
        token,
        title: postTitle || null,
        content: postContent,
        channels: selectedChannels,
        media_urls: postMediaUrls.length > 0 ? postMediaUrls : null,
        status: publish ? 'published' : (scheduledDate ? 'scheduled' : 'draft')
      };

      if (scheduledDate && !publish) {
        const dateTime = new Date(scheduledDate);
        const [hours, minutes] = scheduledTime.split(':');
        dateTime.setHours(parseInt(hours), parseInt(minutes));
        postData.scheduled_at = dateTime.toISOString();
      }

      const res = await fetch(`${API_URL}/api/social-mgmt/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(postData)
      });

      const data = await res.json();

      if (data.success) {
        toast.success(publish ? 'Post published!' : (scheduledDate ? 'Post scheduled!' : 'Draft saved!'));
        setShowComposeDialog(false);
        resetComposeForm();
        fetchPosts();
      } else {
        toast.error(data.message || 'Failed to create post');
      }
    } catch (error) {
      toast.error('Error creating post');
    }
  };

  const resetComposeForm = () => {
    setPostContent('');
    setPostTitle('');
    setSelectedChannels([]);
    setScheduledDate(null);
    setScheduledTime('12:00');
    setPostMediaUrls([]);
    setCaptionVariations([]);
    setSuggestedHashtags([]);
    setShowAiPanel(false);
    setAiTopic('');
  };

  // AI Caption Generation
  const handleGenerateCaption = async () => {
    if (!aiTopic.trim()) {
      toast.error('Please enter a topic for caption generation');
      return;
    }

    setGeneratingCaption(true);
    try {
      const res = await fetch(`${API_URL}/api/social-mgmt/ai/generate-caption?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: aiTopic,
          tone: aiTone,
          platform: aiPlatform,
          include_emojis: true
        })
      });

      const data = await res.json();

      if (data.success) {
        setCaptionVariations(data.variations || []);
        toast.success('Captions generated!');
      } else {
        toast.error(data.detail || 'Failed to generate captions');
      }
    } catch (error) {
      toast.error('Error generating captions');
    } finally {
      setGeneratingCaption(false);
    }
  };

  // AI Hashtag Generation
  const handleGenerateHashtags = async () => {
    const content = postContent || aiTopic;
    if (!content.trim()) {
      toast.error('Please enter content or topic first');
      return;
    }

    setGeneratingHashtags(true);
    try {
      const res = await fetch(`${API_URL}/api/social-mgmt/ai/generate-hashtags?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: content,
          platform: aiPlatform,
          count: 10
        })
      });

      const data = await res.json();

      if (data.success) {
        setSuggestedHashtags(data.hashtags || []);
        toast.success('Hashtags generated!');
      } else {
        toast.error(data.detail || 'Failed to generate hashtags');
      }
    } catch (error) {
      toast.error('Error generating hashtags');
    } finally {
      setGeneratingHashtags(false);
    }
  };

  // Apply caption variation to content
  const applyCaption = (caption) => {
    setPostContent(caption);
    toast.success('Caption applied');
  };

  // Add hashtags to content
  const addHashtagsToContent = () => {
    if (suggestedHashtags.length > 0) {
      const hashtagString = '\n\n' + suggestedHashtags.join(' ');
      setPostContent(prev => prev + hashtagString);
      toast.success('Hashtags added to content');
    }
  };

  const handleDeleteChannel = async (channelId) => {
    if (!window.confirm('Are you sure you want to disconnect this channel?')) return;

    try {
      const res = await fetch(`${API_URL}/api/social-mgmt/channels/${channelId}?token=${token}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success('Channel disconnected');
        fetchChannels();
      } else {
        toast.error(data.message || 'Failed to disconnect channel');
      }
    } catch (error) {
      toast.error('Error disconnecting channel');
    }
  };

  const PlatformIcon = ({ platform, className = "w-5 h-5" }) => {
    const Icon = PLATFORM_ICONS[platform] || Globe;
    return <Icon className={className} />;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="social-media-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Social Media</h1>
          <p className="text-slate-500 dark:text-slate-400">Manage all your social channels in one place</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setShowConnectDialog(true)} data-testid="connect-channel-btn">
            <Plus className="w-4 h-4 mr-2" />
            Connect Channel
          </Button>
          <Button onClick={() => setShowComposeDialog(true)} data-testid="compose-post-btn">
            <Send className="w-4 h-4 mr-2" />
            Compose Post
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Connected Channels</p>
                <p className="text-2xl font-bold">{channels.length}</p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                <Share2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Total Followers</p>
                <p className="text-2xl font-bold">
                  {analytics?.totals?.followers?.toLocaleString() || channels.reduce((sum, c) => sum + (c.follower_count || 0), 0).toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                <Users className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Posts (30 days)</p>
                <p className="text-2xl font-bold">{analytics?.totals?.posts_count || posts.length}</p>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
                <MessageSquare className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Engagement Rate</p>
                <p className="text-2xl font-bold">{analytics?.totals?.engagement_rate || 0}%</p>
              </div>
              <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-full">
                <TrendingUp className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="channels">Channels</TabsTrigger>
          <TabsTrigger value="posts">Posts</TabsTrigger>
          <TabsTrigger value="calendar">Calendar</TabsTrigger>
          <TabsTrigger value="inbox">Inbox</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Connected Channels */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Connected Channels</CardTitle>
                <CardDescription>Your social media accounts</CardDescription>
              </CardHeader>
              <CardContent>
                {channels.length === 0 ? (
                  <div className="text-center py-8">
                    <Share2 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">No channels connected yet</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => setShowConnectDialog(true)}>
                      Connect Your First Channel
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {channels.map((channel) => (
                      <div key={channel.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-full ${PLATFORM_COLORS[channel.platform] || 'bg-slate-500'}`}>
                            <PlatformIcon platform={channel.platform} className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <p className="font-medium">{channel.account_name}</p>
                            <p className="text-sm text-slate-500 capitalize">{channel.platform}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={channel.is_active ? 'success' : 'secondary'}>
                            {channel.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                          <span className="text-sm text-slate-500">{(channel.follower_count || 0).toLocaleString()} followers</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Posts */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Posts</CardTitle>
                <CardDescription>Your latest social media activity</CardDescription>
              </CardHeader>
              <CardContent>
                {posts.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">No posts yet</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => setShowComposeDialog(true)}>
                      Create Your First Post
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {posts.slice(0, 5).map((post) => (
                      <div key={post.id} className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm line-clamp-2">{post.content}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant={
                              post.status === 'published' ? 'success' :
                              post.status === 'scheduled' ? 'warning' : 'secondary'
                            }>
                              {post.status}
                            </Badge>
                            <span className="text-xs text-slate-500">
                              {post.published_at ? format(new Date(post.published_at), 'MMM d, h:mm a') :
                               post.scheduled_at ? `Scheduled: ${format(new Date(post.scheduled_at), 'MMM d, h:mm a')}` :
                               'Draft'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => setShowComposeDialog(true)}>
                  <Send className="w-6 h-6" />
                  <span>New Post</span>
                </Button>
                <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => setActiveTab('calendar')}>
                  <CalendarIcon className="w-6 h-6" />
                  <span>View Calendar</span>
                </Button>
                <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => setActiveTab('inbox')}>
                  <InboxIcon className="w-6 h-6" />
                  <span>Check Inbox</span>
                </Button>
                <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => setShowConnectDialog(true)}>
                  <Plus className="w-6 h-6" />
                  <span>Add Channel</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Channels Tab */}
        <TabsContent value="channels" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Connected Channels</h3>
            <Button onClick={() => setShowConnectDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Connect Channel
            </Button>
          </div>

          {channels.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Share2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Channels Connected</h3>
                <p className="text-slate-500 mb-4">Connect your social media accounts to start publishing</p>
                <Button onClick={() => setShowConnectDialog(true)}>
                  Connect Your First Channel
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {channels.map((channel) => (
                <Card key={channel.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`p-3 rounded-full ${PLATFORM_COLORS[channel.platform] || 'bg-slate-500'}`}>
                          <PlatformIcon platform={channel.platform} className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold">{channel.account_name}</p>
                          <p className="text-sm text-slate-500 capitalize">{channel.platform}</p>
                        </div>
                      </div>
                      <Badge variant={channel.is_active ? 'success' : 'destructive'}>
                        {channel.is_active ? 'Connected' : 'Disconnected'}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <p className="text-2xl font-bold">{(channel.follower_count || 0).toLocaleString()}</p>
                        <p className="text-xs text-slate-500">Followers</p>
                      </div>
                      <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <p className="text-2xl font-bold">{channel.posts_count || 0}</p>
                        <p className="text-xs text-slate-500">Posts</p>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1">
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Sync
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDeleteChannel(channel.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Posts Tab */}
        <TabsContent value="posts" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Posts</h3>
            <div className="flex gap-2">
              <Select defaultValue="all">
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Posts</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="draft">Drafts</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={() => setShowComposeDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                New Post
              </Button>
            </div>
          </div>

          {posts.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <MessageSquare className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Posts Yet</h3>
                <p className="text-slate-500 mb-4">Create your first post to get started</p>
                <Button onClick={() => setShowComposeDialog(true)}>
                  Create Post
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {posts.map((post) => (
                <Card key={post.id}>
                  <CardContent className="py-4">
                    <div className="flex items-start gap-4">
                      <div className="flex-1">
                        {post.title && <h4 className="font-semibold mb-1">{post.title}</h4>}
                        <p className="text-slate-600 dark:text-slate-300">{post.content}</p>
                        
                        <div className="flex items-center gap-3 mt-3">
                          <Badge variant={
                            post.status === 'published' ? 'success' :
                            post.status === 'scheduled' ? 'warning' : 'secondary'
                          }>
                            {post.status}
                          </Badge>
                          
                          <div className="flex items-center gap-1">
                            {post.channels?.map((channelId) => {
                              const channel = channels.find(c => c.id === channelId);
                              if (channel) {
                                return (
                                  <div key={channelId} className={`p-1 rounded-full ${PLATFORM_COLORS[channel.platform]}`}>
                                    <PlatformIcon platform={channel.platform} className="w-3 h-3 text-white" />
                                  </div>
                                );
                              }
                              return null;
                            })}
                          </div>

                          <span className="text-sm text-slate-500">
                            {post.published_at ? format(new Date(post.published_at), 'MMM d, yyyy h:mm a') :
                             post.scheduled_at ? `Scheduled: ${format(new Date(post.scheduled_at), 'MMM d, yyyy h:mm a')}` :
                             `Created: ${format(new Date(post.created_at), 'MMM d, yyyy')}`}
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm">
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Calendar Tab */}
        <TabsContent value="calendar" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Publishing Calendar</CardTitle>
              <CardDescription>View and manage your scheduled posts</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <CalendarIcon className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-semibold mb-2">Calendar View Coming Soon</h3>
                <p className="text-slate-500">Schedule and visualize your content calendar</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Inbox Tab */}
        <TabsContent value="inbox" className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-semibold">Social Inbox</h3>
              <p className="text-sm text-slate-500">Manage comments, messages, and mentions</p>
            </div>
            <Button onClick={() => navigate('/marketing/social/inbox')}>
              <InboxIcon className="w-4 h-4 mr-2" />
              Open Full Inbox
            </Button>
          </div>
          <Card>
            <CardContent className="pt-6">
              <InboxPreview token={token} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Connect Channel Dialog */}
      <Dialog open={showConnectDialog} onOpenChange={setShowConnectDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Connect Social Channel</DialogTitle>
            <DialogDescription>
              Select a platform to connect your account
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3 py-4">
            {platforms.map((platform) => {
              const Icon = PLATFORM_ICONS[platform.id] || Globe;
              return (
                <button
                  key={platform.id}
                  onClick={() => setSelectedPlatform(platform.id)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    selectedPlatform === platform.id
                      ? 'border-accent bg-accent/10'
                      : 'border-slate-200 dark:border-slate-700 hover:border-accent/50'
                  }`}
                >
                  <div className={`p-3 rounded-full ${PLATFORM_COLORS[platform.id] || 'bg-slate-500'} w-fit mx-auto mb-2`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <p className="font-medium text-center">{platform.name}</p>
                </button>
              );
            })}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConnectDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleConnectChannel} disabled={!selectedPlatform}>
              Connect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compose Post Dialog */}
      <Dialog open={showComposeDialog} onOpenChange={setShowComposeDialog}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Post</DialogTitle>
            <DialogDescription>
              Compose and publish to your social channels
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Channel Selection */}
            <div>
              <Label>Select Channels</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {channels.length === 0 ? (
                  <p className="text-sm text-slate-500">No channels connected. Connect a channel first.</p>
                ) : (
                  channels.map((channel) => (
                    <button
                      key={channel.id}
                      onClick={() => {
                        setSelectedChannels(prev =>
                          prev.includes(channel.id)
                            ? prev.filter(id => id !== channel.id)
                            : [...prev, channel.id]
                        );
                      }}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                        selectedChannels.includes(channel.id)
                          ? 'border-accent bg-accent/10'
                          : 'border-slate-200 dark:border-slate-700'
                      }`}
                    >
                      <div className={`p-1 rounded-full ${PLATFORM_COLORS[channel.platform]}`}>
                        <PlatformIcon platform={channel.platform} className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-sm">{channel.account_name}</span>
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Title (optional) */}
            <div>
              <Label>Title (optional)</Label>
              <Input
                placeholder="Post title..."
                value={postTitle}
                onChange={(e) => setPostTitle(e.target.value)}
                className="mt-1"
              />
            </div>

            {/* Content */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Content</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAiPanel(!showAiPanel)}
                  className="text-purple-600 hover:text-purple-700"
                >
                  <Sparkles className="w-4 h-4 mr-1" />
                  AI Assist
                </Button>
              </div>
              <Textarea
                placeholder="What's on your mind?"
                value={postContent}
                onChange={(e) => setPostContent(e.target.value)}
                className="mt-1 min-h-[120px]"
              />
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-slate-500">{postContent.length} characters</p>
                {suggestedHashtags.length > 0 && (
                  <Button type="button" variant="ghost" size="sm" onClick={addHashtagsToContent}>
                    <Hash className="w-3 h-3 mr-1" />
                    Add {suggestedHashtags.length} hashtags
                  </Button>
                )}
              </div>
            </div>

            {/* AI Panel */}
            {showAiPanel && (
              <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg border border-purple-200 dark:border-purple-800 space-y-4">
                <div className="flex items-center gap-2 text-purple-700 dark:text-purple-300">
                  <Wand2 className="w-5 h-5" />
                  <span className="font-medium">AI Content Assistant</span>
                </div>

                {/* AI Input */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label className="text-sm">Topic/Description</Label>
                    <Input
                      placeholder="e.g., New product launch, summer sale, company milestone..."
                      value={aiTopic}
                      onChange={(e) => setAiTopic(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Tone</Label>
                    <Select value={aiTone} onValueChange={setAiTone}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="casual">Casual</SelectItem>
                        <SelectItem value="humorous">Humorous</SelectItem>
                        <SelectItem value="inspirational">Inspirational</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Platform Style</Label>
                    <Select value={aiPlatform} onValueChange={setAiPlatform}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="instagram">Instagram</SelectItem>
                        <SelectItem value="facebook">Facebook</SelectItem>
                        <SelectItem value="twitter">Twitter</SelectItem>
                        <SelectItem value="linkedin">LinkedIn</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* AI Action Buttons */}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleGenerateCaption}
                    disabled={generatingCaption || !aiTopic.trim()}
                    className="flex-1"
                  >
                    {generatingCaption ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-1" />
                    )}
                    Generate Caption
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleGenerateHashtags}
                    disabled={generatingHashtags || (!postContent.trim() && !aiTopic.trim())}
                    className="flex-1"
                  >
                    {generatingHashtags ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Hash className="w-4 h-4 mr-1" />
                    )}
                    Generate Hashtags
                  </Button>
                </div>

                {/* Caption Variations */}
                {captionVariations.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm">Caption Options (click to use)</Label>
                    {captionVariations.map((variation) => (
                      <button
                        key={variation.id}
                        type="button"
                        onClick={() => applyCaption(variation.caption)}
                        className="w-full text-left p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-purple-400 transition-colors"
                      >
                        <p className="text-sm line-clamp-3">{variation.caption}</p>
                        <p className="text-xs text-slate-400 mt-1">{variation.char_count} characters</p>
                      </button>
                    ))}
                  </div>
                )}

                {/* Suggested Hashtags */}
                {suggestedHashtags.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm">Suggested Hashtags</Label>
                    <div className="flex flex-wrap gap-1">
                      {suggestedHashtags.map((tag, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Schedule */}
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Label>Schedule (optional)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full mt-1 justify-start">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {scheduledDate ? format(scheduledDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={scheduledDate}
                      onSelect={setScheduledDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              {scheduledDate && (
                <div className="w-32">
                  <Label>Time</Label>
                  <Input
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    className="mt-1"
                  />
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowComposeDialog(false)}>
              Cancel
            </Button>
            <Button variant="outline" onClick={() => handleCreatePost(false)}>
              Save Draft
            </Button>
            {scheduledDate ? (
              <Button onClick={() => handleCreatePost(false)}>
                <Clock className="w-4 h-4 mr-2" />
                Schedule
              </Button>
            ) : (
              <Button onClick={() => handleCreatePost(true)}>
                <Send className="w-4 h-4 mr-2" />
                Publish Now
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Inbox Preview Component
const InboxPreview = ({ token }) => {
  const [interactions, setInteractions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [interRes, statsRes] = await Promise.all([
          fetch(`${API_URL}/api/social-mgmt/inbox?token=${token}&limit=5`),
          fetch(`${API_URL}/api/social-mgmt/inbox/stats?token=${token}`)
        ]);
        
        const interData = await interRes.json();
        const statsData = await statsRes.json();
        
        if (interData.success) setInteractions(interData.interactions || []);
        if (statsData.success) setStats(statsData.stats);
      } catch (error) {
        console.error('Error fetching inbox preview:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <p className="text-2xl font-bold text-blue-600">{stats?.total_open || 0}</p>
          <p className="text-sm text-slate-500">Open</p>
        </div>
        <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <p className="text-2xl font-bold text-orange-600">{stats?.unassigned || 0}</p>
          <p className="text-sm text-slate-500">Unassigned</p>
        </div>
        <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <p className="text-2xl font-bold text-purple-600">{stats?.my_assigned || 0}</p>
          <p className="text-sm text-slate-500">Assigned to me</p>
        </div>
      </div>

      {/* Recent Interactions */}
      {interactions.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <InboxIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No interactions yet</p>
          <p className="text-sm">Connect social channels to start receiving messages</p>
        </div>
      ) : (
        <div className="space-y-2">
          {interactions.map((interaction) => {
            const Icon = PLATFORM_ICONS[interaction.platform] || Globe;
            return (
              <div key={interaction.id} className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className={`p-2 rounded-full ${PLATFORM_COLORS[interaction.platform] || 'bg-slate-500'}`}>
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{interaction.author_name || 'Unknown'}</span>
                    <Badge variant="outline" className="text-xs">{interaction.interaction_type}</Badge>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-1">{interaction.content}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SocialMediaDashboard;
